///<reference path="../../../../../Builder/StylesheetManager.ts" />
///<reference path="../../../../../Builder/Common/AlertManager.ts" />
import { LitElement, html, css, nothing } from "lit";

// --- Default Data ---
const DEFAULT_PREDEFINED_FONTS = [
  // System Fonts (Web Safe)
  { name: "Arial", category: "Sans Serif", type: "system", price: "Free", rating: 5.0, downloads: "∞" },
  { name: "Verdana", category: "Sans Serif", type: "system", price: "Free", rating: 5.0, downloads: "∞" },

  // Web Fonts
  { name: "Roboto", source: "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2", category: "Sans Serif", weight: "400", style: "normal", price: "Free", rating: 4.8, downloads: "120k" },
  { name: "Open Sans", source: "https://fonts.gstatic.com/s/opensans/v35/memvYaGs126MiZpBA-UvWbX2vVnXBbObj2OVTS-muw.woff2", category: "Sans Serif", weight: "400", style: "normal", price: "Free", installLabel: "Add", rating: 4.6, downloads: "95k" },
  { name: "Merriweather", source: "https://fonts.gstatic.com/s/merriweather/v30/u-440qyriQwlOrhSvowK_l5-fCzm.woff2", category: "Serif", weight: "400", style: "normal", price: "$12", rating: 4.9, downloads: "42k" },
  { name: "Pacifico", source: "https://fonts.gstatic.com/s/pacifico/v22/FwZY7-Qmy14u9lezJ-6H6MmTpA.woff2", category: "Handwriting", weight: "400", style: "normal", price: "$15", promoTag: "Popular", rating: 4.7, downloads: "200k" },
  { name: "Lato", source: "https://fonts.gstatic.com/s/lato/v24/S6uyw4BMUTPHjx4wXg.woff2", category: "Sans Serif", weight: "400", style: "normal", price: "$20", salePrice: "$10", promoTag: "50% OFF", rating: 4.5, downloads: "80k" },
  { name: "Montserrat", source: "https://fonts.gstatic.com/s/montserrat/v25/JTUSjIg1_i6t8kCHKm459Wlhyw.woff2", category: "Sans Serif", weight: "400", style: "normal", price: "Free", locked: true, installLabel: "Premium", rating: 5.0, downloads: "300k" },
];

export class RDFontManager extends LitElement {
  static properties = {
    predefinedFonts: { type: Array },
    asDialog: { type: Boolean, reflect: true },
    isOpen: { type: Boolean, reflect: true },
    autoInject: { type: Boolean },

    // Feature Flags
    enableSearch: { type: Boolean },
    enableSorting: { type: Boolean },

    onFontSelected: { type: Object },
    onFontUploaded: { type: Object },
    onFontImported: { type: Object },
    onFontRemoved: { type: Object },
    onFontInstalled: { type: Object },
    onFontRated: { type: Object },
    onClose: { type: Object },
    onOpen: { type: Object },

    fonts: { type: Array },
    selectedFont: { type: Object },

    activeTab: { type: String, reflect: true },
    newFontName: { type: String },
    newFontUrl: { type: String },
    newFontFile: { type: Object },
    isLoading: { type: Boolean, reflect: true },
    errorMessage: { type: String },

    searchQuery: { type: String },
    sortOption: { type: String, reflect: true },

    _scrollTop: { type: Number },
    _containerWidth: { type: Number },
    _containerHeight: { type: Number },
  };

  static styles = css`
    :host {
      display: block;
      font-family: var(--body-font-family, sans-serif);
      color: var(--body-color);
      box-sizing: border-box;
      font-size: 14px;
    }
    *,
    *:before,
    *:after {
      box-sizing: inherit;
    }

    .manager-container {
      background-color: var(--body-bg);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      box-shadow: var(--box-shadow);
      max-width: 900px;
      margin: 32px auto;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      height: 80vh;
      position: relative;
    }

    /* Layout */
    .header,
    .footer,
    .tabs,
    .toolbar {
      flex: 0 0 auto;
    }

    .header {
      padding: 24px;
      border-bottom: 1px solid var(--border-color);
      background-color: var(--secondary-bg);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .header h2 {
      margin: 0;
      font-size: 20px;
      color: var(--heading-color, inherit);
    }

    /* Toolbar */
    .toolbar {
      padding: 12px 24px;
      background-color: var(--body-bg);
      border-bottom: 1px solid var(--border-color);
      display: flex;
      gap: 12px;
      align-items: center;
    }
    .search-input-wrapper {
      position: relative;
      flex: 1;
    }
    .search-input {
      width: 100%;
      padding: 8px 12px 8px 36px;
      border-radius: 6px;
      border: 1px solid var(--border-color);
      background-color: var(--white-color);
      color: var(--body-color);
      font-size: 14px;
      box-sizing: border-box;
    }
    .search-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      color: var(--secondary-color);
      pointer-events: none;
    }
    .sort-select {
      padding: 8px 12px;
      border-radius: 6px;
      border: 1px solid var(--border-color);
      background-color: var(--white-color);
      color: var(--body-color);
      font-size: 14px;
      cursor: pointer;
      min-width: 140px;
    }

    .tabs {
      display: flex;
      background-color: var(--body-bg);
      border-bottom: 1px solid var(--border-color);
    }
    .tab-btn {
      flex: 1;
      padding: 16px;
      border: none;
      background: none;
      cursor: pointer;
      font-weight: 500;
      color: var(--secondary-color);
      border-bottom: 3px solid transparent;
      transition: all var(--transition-speed);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 14px;
    }
    .tab-btn:hover {
      background-color: var(--tertiary-bg);
      color: var(--primary-color);
    }
    .tab-btn.active {
      color: var(--primary-color);
      border-bottom-color: var(--primary-color);
    }
    .tab-badge {
      background-color: var(--tertiary-bg);
      border: 1px solid var(--border-color);
      border-radius: 12px;
      padding: 2px 8px;
      font-size: 12px;
      color: var(--body-color);
    }
    .tab-btn.active .tab-badge {
      background-color: var(--primary-color);
      color: #fff;
      border-color: var(--primary-color);
    }

    /* Status Bar inside content */
    .list-status-bar {
      padding-bottom: 16px;
      color: var(--secondary-color);
      font-size: 13px;
      font-weight: 500;
    }

    .content {
      flex: 1;
      overflow-y: auto;
      padding: 24px;
      position: relative;
      contain: strict;
    }
    .font-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
      gap: 16px;
    }

    .font-card {
      background-color: var(--white-color);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      transition: transform 0.2s, box-shadow 0.2s, border-color 0.15s;
      position: relative;
      height: 260px;
      box-sizing: border-box;
      overflow: hidden;
      cursor: default;
      padding: 0;
    }
    .font-card.installed {
      cursor: pointer;
    }
    :host-context([data-theme="dark"]) .font-card {
      background-color: var(--secondary-bg);
    }
    .font-card:hover {
      transform: translateY(-2px);
      box-shadow: var(--box-shadow-sm);
      border-color: var(--primary-color);
    }
    .font-card.selected {
      border-color: var(--primary-color);
      box-shadow: inset 0 0 0 1px var(--primary-color);
    }
    .font-card.selected::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 2px solid var(--primary-color);
      border-radius: 8px;
      pointer-events: none;
      z-index: 10;
    }

    .card-header {
      padding: 16px 16px 0 16px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-shrink: 0;
    }
    .font-name-group {
      display: flex;
      flex-direction: column;
      gap: 4px;
      overflow: hidden;
    }
    .font-name {
      font-weight: 700;
      font-size: 18px;
      color: var(--heading-color);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-right: 8px;
      letter-spacing: -0.02em;
    }
    .font-category {
      font-size: 11px;
      color: var(--secondary-color);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-weight: 600;
    }
    .promo-badge {
      font-size: 10px;
      font-weight: 700;
      color: #fff;
      background: linear-gradient(135deg, #f59e0b, #d97706);
      padding: 3px 8px;
      border-radius: 12px;
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(245, 158, 11, 0.2);
    }

    .font-preview {
      flex: 1 1 auto;
      min-height: 0;
      font-size: 24px;
      line-height: 1.3;
      padding: 0 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      word-break: break-word;
      font-family: var(--body-font-family);
      color: var(--body-color);
      pointer-events: none;
      font-size: 20px;
    }

    .card-footer {
      padding: 12px 16px;
      border-top: 1px solid var(--border-color);
      background-color: var(--tertiary-bg);
      display: flex;
      justify-content: space-between;
      align-items: center;
      min-height: 48px;
      flex-shrink: 0;
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      padding: 4px 8px;
      border-radius: 6px;
      font-size: 12px;
      font-weight: 500;
      color: var(--secondary-color);
    }
    .status-badge.installed {
      color: var(--body-color);
      background-color: var(--white-color);
      border: 1px solid var(--border-color);
      opacity: 0.8;
    }
    :host-context([data-theme="dark"]) .status-badge.installed {
      background-color: var(--tertiary-bg);
    }
    .status-badge.price {
      color: var(--body-color);
      font-weight: 600;
      opacity: 0.8;
    }
    .status-badge.system {
      background-color: #f3f4f6;
      color: #6b7280;
      border: 1px solid #e5e7eb;
      font-weight: 600;
    }

    /* Stats Row & Rating */
    .stats-row {
      display: flex;
      padding: 0 16px 8px 16px;
      gap: 12px;
      font-size: 12px;
      color: var(--secondary-color);
      flex-shrink: 0;
      align-items: center;
      height: 20px;
    }
    .stat-item {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .stat-icon {
      width: 12px;
      height: 12px;
      fill: currentColor;
      opacity: 0.7;
    }

    /* Interactive Rating CSS */
    .rating-wrapper {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .rating-stars {
      display: flex;
      flex-direction: row-reverse;
      cursor: pointer;
    }
    .rating-star {
      width: 14px;
      height: 14px;
      fill: #d1d5db;
      transition: fill 0.1s, transform 0.1s;
    }
    .rating-star.filled {
      fill: #f59e0b;
    }
    .rating-stars:hover .rating-star {
      fill: #d1d5db;
    }
    .rating-stars .rating-star:hover,
    .rating-stars .rating-star:hover ~ .rating-star {
      fill: #f59e0b;
      transform: scale(1.1);
    }
    .rating-text {
      margin-left: 4px;
      font-weight: 600;
      color: var(--body-color);
      font-size: 11px;
    }

    .price-block {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .sale-price {
      font-weight: 700;
      color: var(--danger-color);
      font-size: 14px;
    }
    .original-price {
      text-decoration: line-through;
      color: var(--secondary-color);
      font-size: 12px;
    }
    .regular-price {
      font-weight: 600;
      color: var(--body-color);
      font-size: 14px;
    }

    .font-actions {
      margin-left: auto;
    }

    /* Generic UI */
    .footer {
      padding: 16px 24px;
      border-top: 1px solid var(--border-color);
      background-color: var(--secondary-bg);
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }
    .btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 8px 16px;
      font-weight: 500;
      border-radius: 6px;
      cursor: pointer;
      border: 1px solid transparent;
      transition: background-color 0.15s, opacity 0.15s;
      font-size: 14px;
    }
    .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .btn-sm {
      padding: 6px 14px;
      font-size: 12px;
    }
    .btn-primary {
      background-color: var(--primary-color);
      color: var(--text-on-primary, #fff);
    }
    .btn-primary:hover:not(:disabled) {
      background-color: var(--primary-color-dark);
    }
    .btn-secondary {
      background-color: transparent;
      border: 1px solid var(--secondary-color);
      color: var(--secondary-color);
    }
    .btn-success {
      background-color: var(--dark-color);
      color: var(--white-color);
    }
    .btn-success:hover {
      background-color: var(--dark-color-light);
    }
    .btn-danger {
      background-color: white;
      color: var(--danger-color);
      border: 1px solid var(--border-color);
    }
    .btn-danger:hover {
      background-color: #fef2f2;
      border-color: var(--danger-color);
    }

    .loader {
      border: 2px solid rgba(0, 0, 0, 0.1);
      border-radius: 50%;
      border-top: 2px solid currentColor;
      width: 16px;
      height: 16px;
      animation: spin 0.8s linear infinite;
      display: inline-block;
    }
    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.7);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 10;
      backdrop-filter: blur(1px);
    }
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    .dialog-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      backdrop-filter: blur(2px);
    }
    .dialog-overlay .manager-container {
      margin: 0;
      width: 90vw;
      height: 85vh;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    }
    .btn-close {
      background: none;
      border: none;
      color: var(--secondary-color);
      font-size: 28px;
      cursor: pointer;
      padding: 0 8px;
      margin-left: auto;
      line-height: 1;
    }

    .import-section {
      margin-bottom: 32px;
      padding-bottom: 32px;
      border-bottom: 1px solid var(--border-color);
    }
    .import-section:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }
    .form-group {
      margin-bottom: 16px;
    }
    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      color: var(--body-color);
    }
    input {
      width: 100%;
      padding: 8px 12px;
      border-radius: 6px;
      border: 1px solid var(--border-color);
      background-color: var(--white-color);
      color: var(--body-color);
      font-family: inherit;
      font-size: 14px;
      box-sizing: border-box;
    }
    .upload-area {
      border: 2px dashed var(--border-color);
      border-radius: 6px;
      padding: 32px;
      text-align: center;
      margin-bottom: 16px;
      cursor: pointer;
      background-color: var(--tertiary-bg);
    }
    .error-msg {
      color: var(--danger-color);
      /* margin-bottom: 16px;*/
      font-size: 14px;
      /* background: var(--danger-bg-subtle);*/
      padding: 8px;
      border-radius: 6px;
      position: absolute;
      left: 24px;
    }
    .empty-state {
      text-align: center;
      padding: 48px;
      color: var(--secondary-color);
    }
  `;

  constructor() {
    super();
    this.predefinedFonts = []; // DEFAULT_PREDEFINED_FONTS;
    this.fonts = [];
    // this.installedFonts = [];
    this.selectedFont = null;
    this.activeTab = "installed";
    this.newFontName = "";
    this.newFontUrl = "";
    this.newFontFile = null;
    this.isLoading = false;
    this.errorMessage = "";
    this.asDialog = false;
    this.isOpen = false;
    this.autoInject = true;

    this.enableSearch = true;
    this.enableSorting = true;
    this.searchQuery = "";
    this.sortOption = "popular";

    this._scrollTop = 0;
    this._containerHeight = 0;
    this._containerWidth = 0;
    this._loadedFontNames = new Set();
  }
  get installedFonts() {
    return this.fonts.filter((f) => f.installed);
  }

  firstUpdated() {
    this._initFonts();
    this._initVirtualizer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }

  updated(changedProperties) {
    if (changedProperties.has("isOpen") && this.asDialog) {
      if (this.isOpen) {
        const container = this.shadowRoot.getElementById("virtual-scroll-container");
        if (container) container.scrollTop = 0;
        this._scrollTop = 0;
        this._initVirtualizer();
        if (this.onOpen && typeof this.onOpen === "function") this.onOpen();
      } else {
        if (this.onClose && typeof this.onClose === "function") this.onClose();
        this.searchQuery = "";
        this.sortOption = "popular";
      }
    }

    if (changedProperties.has("activeTab")) {
      const container = this.shadowRoot.getElementById("virtual-scroll-container");
      if (container) container.scrollTop = 0;
      this._scrollTop = 0;
      this.requestUpdate();
    }
  }

  _initVirtualizer() {
    const container = this.shadowRoot.querySelector(".content");
    if (container) {
      this._containerHeight = container.clientHeight;
      this._containerWidth = container.clientWidth;

      if (!this._resizeObserver) {
        this._resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            this._containerWidth = entry.contentRect.width;
            this._containerHeight = entry.contentRect.height;
            requestAnimationFrame(() => {
              this.requestUpdate();
            });
          }
        });
        this._resizeObserver.observe(container);
      }
    }
  }

  _initFonts() {
    const jsonFonts = this.predefinedFonts || []; // DEFAULT_PREDEFINED_FONTS;
    const mappedFonts = jsonFonts.map((f) => ({
      ...f,
      type: f.type || "predefined",
      // Auto-install if system type, otherwise preserve existing state
      installed: f.type === "system" ? true : f.installed || false,
      weight: f.weight || "400",
      style: f.style || "normal",
      price: f.price || "Free",
      salePrice: f.salePrice || null,
      promoTag: f.promoTag || null,
      installLabel: f.installLabel || (f.type === "system" ? "System" : "Install"),
      locked: f.locked || false,
      rating: f.rating || (3 + Math.random() * 2).toFixed(1),
      downloads: f.downloads || Math.floor(Math.random() * 100) + "k",
      userRating: 0,
      // System fonts are not removable by default, unless overridden. Other fonts removable by default.
      removable: f.type === "system" ? false : f.removable !== false,
    }));
    this.fonts = [...this.fonts, ...mappedFonts];
  }

  _handleVirtualScroll(e) {
    this._scrollTop = e.target.scrollTop;
  }

  _getFilteredFonts() {
    let filtered = this.fonts;
    if (this.activeTab === "installed") {
      filtered = filtered.filter((f) => f.installed);
    } else if (this.activeTab === "marketplace") {
      // Show all predefined fonts (store items), but exclude system fonts
      filtered = filtered.filter((f) => f.type === "predefined");
    } else {
      return [];
    }

    if (this.enableSearch && this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      filtered = filtered.filter((f) => f.name.toLowerCase().includes(q));
    }

    if (this.enableSorting && this.sortOption) {
      filtered = [...filtered].sort((a, b) => {
        switch (this.sortOption) {
          case "name":
            return a.name.localeCompare(b.name);
          case "rating":
            return b.rating - a.rating;
          case "popular":
            if (a.downloads === "∞" || b.downloads === "∞") return 0;
            const parseDl = (s) => parseInt(s.replace(/k/g, "000").replace(/m/g, "000000"));
            return parseDl(b.downloads) - parseDl(a.downloads);
          case "newest":
            return 0;
          default:
            return 0;
        }
      });
    }
    return filtered;
  }

  _handleSearch(e) {
    this.searchQuery = e.target.value;
    this.requestUpdate();
  }

  _handleSort(e) {
    this.sortOption = e.target.value;
    this.requestUpdate();
  }

  // --- Rating Logic ---
  rateFont(fontName, rating) {
    const font = this.fonts.find((f) => f.name === fontName);
    if (!font || !font.installed) return;

    this.fonts = this.fonts.map((f) => (f.name === fontName ? { ...f, userRating: rating } : f));
    this._emitEvent("font-rated", { ...font, userRating: rating });
    this.requestUpdate();
  }

  renderVirtualGrid(fontList) {
    if (!fontList || fontList.length === 0) {
      return html`<div class="empty-state">No fonts found matching your search.</div>`;
    }

    const CARD_HEIGHT = 260;
    const GAP = 16;
    const MIN_CARD_WIDTH = 240;

    const paddingX = 48;
    const availWidth = (this._containerWidth || 900) - paddingX;
    const cols = Math.floor((availWidth + GAP) / (MIN_CARD_WIDTH + GAP)) || 1;

    const totalRows = Math.ceil(fontList.length / cols);
    const totalHeight = totalRows * (CARD_HEIGHT + GAP) - GAP;

    const buffer = 2;
    const rowHeight = CARD_HEIGHT + GAP;
    const startRow = Math.max(0, Math.floor(this._scrollTop / rowHeight) - buffer);
    const visibleRowCount = Math.ceil((this._containerHeight || 600) / rowHeight) + buffer * 2;
    const endRow = Math.min(totalRows, startRow + visibleRowCount);

    const startIndex = startRow * cols;
    const endIndex = Math.min(fontList.length, endRow * cols);
    const visibleData = fontList.slice(startIndex, endIndex);

    requestAnimationFrame(() => {
      visibleData.forEach((font) => {
        // Skip network load for system fonts or already loaded fonts
        if (!this._loadedFontNames.has(font.name) && font.type !== "system") {
          this._loadedFontNames.add(font.name);
          this._loadFontToDocument(font.name, font.source).catch(() => {});
        }
      });
    });

    let offsetY = startRow * rowHeight;
    // if (this._scrollTop == 0) {
    //   offsetY = 0;
    // }

    // --- Status Bar Update ---
    const isSearching = !!this.searchQuery;
    const count = fontList.length;
    const typeLabel = this.activeTab === "installed" ? "Installed Fonts" : "Marketplace Fonts";

    let statusText = "";
    if (isSearching) {
      statusText = `${count} result${count !== 1 ? "s" : ""} found for "${this.searchQuery}"`;
    } else {
      statusText = `${count} ${typeLabel}`;
    }

    return html`
      <div class="list-status-bar">${statusText}</div>
      <div style="height: ${totalHeight}px; position: relative;">
        <div class="font-grid" style="position: absolute; top: 0; left: 0; right: 0; transform: translateY(${offsetY}px);">${visibleData.map((font) => this.renderFontCard(font))}</div>
      </div>
    `;
  }

  open() {
    this.isOpen = true;
  }
  close() {
    this.isOpen = false;
    this._emitEvent("close", {});
  }

  installFont(fontName) {
    const font = this.fonts.find((f) => f.name === fontName);
    if (font && font.locked) return;

    this.fonts = this.fonts.map((f) => (f.name === fontName ? { ...f, installed: true } : f));

    if (font) {
      if (font.type !== "system") {
        this._loadedFontNames.add(font.name);
        this._loadFontToDocument(font.name, font.source);
      }
      this._emitEvent("font-installed", font);
    }
  }

  removeFont(fontName) {
    const fontIndex = this.fonts.findIndex((f) => f.name === fontName);
    if (fontIndex === -1) return;
    const font = this.fonts[fontIndex];

    if (this.selectedFont && this.selectedFont.name === fontName) {
      this.selectedFont = null;
    }
    let isUsed = StylesheetManager.fonts.some((f) => f.name === fontName);
    if (isUsed) {
      AlertManager.confirm(
        `The font <b>${fontName}</b> is currently in use. Removing it will affect everyone on the project.
<br><br>
Existing elements will retain this font, but it will no longer be available to select for new elements. Do you want to proceed?`,
        "Confirmation"
      ).then((res) => {
        if (res.action === "confirm") {
          //StylesheetManager.removeFontByName(fontName);
          _remove.call(this);
        }
      });
      return;
    } else {
      _remove.call(this);
    }
    function _remove() {
      if (font.type === "predefined" || font.type === "system") {
        const updated = [...this.fonts];
        updated[fontIndex] = { ...font, installed: false, userRating: 0 };
        this.fonts = updated;
      } else {
        this.fonts = this.fonts.filter((f) => f.name !== fontName);
      }
      this._emitEvent("font-removed", font);
    }
  }

  selectFont(fontName) {
    const font = this.fonts.find((f) => f.name === fontName);
    if (!font || !font.installed) return;

    const css = this._generateFontFaceCss(font);
    const fontWithCss = { ...font, css };

    this.selectedFont = fontWithCss;
    if (this.autoInject) this._injectIntoMainDocument(fontWithCss);
    this._emitEvent("font-selected", fontWithCss);
  }

  _injectIntoMainDocument(font) {
    // If system font, no CSS injection needed generally, but we can clear styles if needed.
    // Or just assume font-family is sufficient.
    // The _generateFontFaceCss returns a comment for system fonts, so style tag will be empty or comment.
    const styleId = "font-manager-selected-css";
    let styleTag = document.getElementById(styleId);
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = font.css;
  }

  async addCustomFont(name, url, options = {}) {
    if (this.fonts.some((f) => f.name.toLowerCase() === name.trim().toLowerCase())) return;
    try {
      const result = await this._loadFontToDocument(name, url);
      const finalUrl = result.source || url;
      const finalName = result.meta?.family ? result.meta.family.replace(/['"]/g, "") : name;
      if (!finalUrl || !finalName) {
        this.errorMessage = "Failed to load font from the provided URL.";
        return "error";
      }
      //if the font is already in the marketplace then install it instead of adding a new one
      if (this.fonts.some((f) => f.name.toLowerCase() === finalName.trim().toLowerCase())) {
        this.installFont(finalName);
        return;
      }

      const newFont = {
        name: finalName,
        source: finalUrl,
        originalSource: url,
        type: options.type || "url",
        category: options.category || "Custom",
        weight: result.meta?.weight || "400",
        style: result.meta?.style || "normal",
        price: "Free",
        installed: true,
        rating: "New",
        downloads: "0",
        userRating: 0,
        removable: true,
      };
      this.fonts = [...this.fonts, newFont];
      this._loadedFontNames.add(finalName);
      this._emitEvent("font-imported", newFont);
      return "success";
    } catch (err) {
      console.error("Failed to add font", err);
      return "error";
    }
  }

  _handleOverlayClick(e) {
    if (e.target.classList.contains("dialog-overlay")) this.close();
  }

  _emitEvent(eventName, detail) {
    this.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true, composed: true }));
    const camelCase =
      "on" +
      eventName
        .split("-")
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join("");
    if (this[camelCase] && typeof this[camelCase] === "function") this[camelCase](detail);
  }

  _generateFontFaceCss(font) {
    if (font.type === "system") return `/* System Font: ${font.name} */`;

    const src = font.originalSource || font.source;
    const isCss = src.includes("fonts.googleapis.com") || src.toLowerCase().endsWith(".css") || src.includes("/css/");
    if (isCss) return `@import url('${src}');`;
    return `@font-face { font-family: '${font.name}'; font-style: ${font.style}; font-weight: ${font.weight}; font-display: swap; src: url(${font.source}) format('woff2'); }`;
  }

  async _loadFontToDocument(name, source) {
    // Skip network for system fonts
    if (!source) return { source: null, meta: {} };

    let finalSource = source;
    let meta = { family: null, weight: null, style: null };
    const isCss = source.includes("fonts.googleapis.com") || source.toLowerCase().endsWith(".css") || source.includes("/css/");

    if (isCss) {
      try {
        const response = await fetch(source);
        if (!response.ok) throw new Error("Network err");
        const cssText = await response.text();
        const blocks = cssText.matchAll(/@font-face\s*{([^}]+)}/g);
        for (const match of blocks) {
          const content = match[1];
          const srcMatch = content.match(/src:[^;]*url\((?:'|")?([^'")]+)(?:'|")?\)/);
          if (srcMatch && srcMatch[1]) {
            finalSource = srcMatch[1];
            const f = content.match(/font-family:\s*['"]?([^;'"}]+)['"]?/);
            if (f) meta.family = f[1];
            const w = content.match(/font-weight:\s*([^;'"}]+)/);
            if (w) meta.weight = w[1];
            const s = content.match(/font-style:\s*([^;'"}]+)/);
            if (s) meta.style = s[1];
            break;
          }
        }
      } catch (err) {
        await this._injectCssLink(name, source);
        return { source: null, meta };
      }
    }

    try {
      const fam = meta.family ? meta.family.replace(/['"]/g, "") : name;
      const fontFace = new FontFace(fam, `url(${finalSource})`, {
        display: "swap",
        weight: meta.weight || "400",
        style: meta.style || "normal",
      });
      document.fonts.add(fontFace);
      await fontFace.load();
      return { source: finalSource, meta };
    } catch (e) {
      if (isCss) {
        await this._injectCssLink(name, source);
        return { source: null, meta };
      }
      throw new Error(`Load failed: ${finalSource}`);
    }
  }

  _injectCssLink(name, url) {
    if (document.querySelector(`link[href="${url}"]`)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = url;
      link.onload = () => resolve(true);
      link.onerror = () => reject(new Error("CSS load failed"));
      document.head.appendChild(link);
    });
  }

  _handleTabChange(tab) {
    this.activeTab = tab;
    this.errorMessage = "";
    this.searchQuery = "";
  }

  async _handleUrlImport(e) {
    e.preventDefault();
    //if (!this.newFontName || !this.newFontUrl) return;
    if (!this.newFontUrl) {
      this.errorMessage = "Please provide a font URL.";
      this.requestUpdate();
      return;
    }
    this.isLoading = true;
    try {
      let res = await this.addCustomFont(this.newFontName, this.newFontUrl, { type: "url" });
      if (res === "error") {
        this.errorMessage = "Failed to load font from the provided URL.";
        this.isLoading = false;
        return;
      }
      this.newFontUrl = "";
      this.activeTab = "installed"; // Go to installed after import
    } catch (err) {
      this.errorMessage = "Failed to load font from the provided URL.";
    } finally {
      this.isLoading = false;
    }
  }

  _handleFileSelect(e) {
    const file = e.target.files[0];
    if (file) {
      this.newFontFile = file;
      // if (!this.newFontName)
      this.newFontName = file.name.replace(/\.[^/.]+$/, "");
    }
  }

  async _handleFileUpload(e) {
    e.preventDefault();
    if (!this.newFontName || !this.newFontFile) return;
    this.isLoading = true;
    try {
      const objectUrl = URL.createObjectURL(this.newFontFile);
      let res = await this.addCustomFont(this.newFontName, objectUrl, { type: "upload" });
      if (res === "error") {
        this.errorMessage = "Failed to parse font file.";
        this.isLoading = false;
        return;
      }

      const added = this.fonts[this.fonts.length - 1];
      this._emitEvent("font-uploaded", added);
      this.activeTab = "installed"; // Go to installed after upload
    } catch (err) {
      this.errorMessage = "Failed to parse font file.";
    } finally {
      this.isLoading = false;
      this.isLoading = false;
      this.newFontFile = null;
      this.newFontName = "";
    }
  }

  _handleSearch(e) {
    this.searchQuery = e.target.value;
    this.requestUpdate();
  }

  _handleSort(e) {
    this.sortOption = e.target.value;
    this.requestUpdate();
  }
  _handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  _handleFileDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      this.newFontFile = file;
      // if (!this.newFontName)
      this.newFontName = file.name.replace(/\.[^/.]+$/, "");
    }
  }
  // --- Render Rating Stars ---
  renderStars(font) {
    return html`
      <div class="rating-wrapper" title="My Rating: ${font.userRating || 0}" @click=${(e) => e.stopPropagation()}>
        <div class="rating-stars">
          ${[5, 4, 3, 2, 1].map(
            (i) => html`
              <svg class="rating-star interactive ${i <= font.userRating ? "filled" : ""}" viewBox="0 0 24 24" @click=${() => this.rateFont(font.name, i)}>
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
            `
          )}
        </div>
        ${font.userRating > 0 ? html`<span class="rating-text">${font.userRating}</span>` : html`<span class="rating-text" style="font-weight:400;font-size:11px;color:#999">Rate</span>`}
      </div>
    `;
  }

  renderFontCard(font) {
    const isSelected = this.selectedFont && this.selectedFont.name === font.name;
    return html` <div class="font-card ${font.installed ? "installed" : ""} ${isSelected ? "selected" : ""}" @click=${() => (font.installed ? this.selectFont(font.name) : null)}>
      <div class="card-header">
        <div class="font-name-group">
          <div class="font-name" title="${font.name}">${font.name}</div>
          <span class="font-category">${font.category || "Custom"}</span>
        </div>
        ${font.promoTag ? html`<span class="promo-badge">${font.promoTag}</span>` : nothing}
      </div>

      <div class="font-preview" style="font-family: '${font.name}', sans-serif;">The quick brown fox jumps over the lazy dog.</div>

      <div class="stats-row">
        ${font.installed
          ? html` ${this.renderStars(font)}
              <div class="stat-item" title="Global Rating">
                <svg class="stat-icon" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                <span>${font.rating || "N/A"}</span>
              </div>
              <div class="stat-item" title="Downloads">
                <svg class="stat-icon" viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
                <span>${font.downloads || 0}</span>
              </div>`
          : html` <div class="stat-item" title="Avg Rating">
                <svg class="stat-icon" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                <span>${font.rating || "N/A"}</span>
              </div>
              <div class="stat-item" title="Downloads">
                <svg class="stat-icon" viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
                <span>${font.downloads || 0}</span>
              </div>`}
      </div>

      <div class="card-footer">
        ${font.installed
          ? html` <span class="status-badge installed">Installed</span>`
          : html` <div class="price-block">
              ${font.salePrice
                ? html`
                    <span class="sale-price">${font.salePrice}</span>
                    <span class="original-price">${font.price}</span>
                  `
                : html`<span class="regular-price">${font.price || "Free"}</span>`}
            </div>`}

        <div class="font-actions">
          ${!font.installed
            ? html` <button
                class="btn btn-sm btn-success"
                ?disabled=${font.locked}
                @click=${(e) => {
                  e.stopPropagation();
                  this.installFont(font.name);
                }}
              >
                ${font.installLabel || "Install"}
              </button>`
            : font.removable !== false && font.type !== "system"
            ? html` <button
                class="btn btn-sm btn-danger"
                @click=${(e) => {
                  e.stopPropagation();
                  this.removeFont(font.name);
                }}
              >
                ${font.type === "predefined" ? "Uninstall" : "Delete"}
              </button>`
            : html`<span class="status-badge system">System</span>`}
        </div>
      </div>
    </div>`;
  }

  renderImportPage() {
    return html`
      <div class="content" style="padding: 32px;">
        <div class="import-section">
          <h3>Upload Custom Font</h3>
          <p style="color: var(--secondary-color); font-size: 14px; margin-bottom: 16px;">Select a local font file (.ttf, .otf, .woff, .woff2).</p>
          <form @submit=${this._handleFileUpload}>
            <div class="upload-area" @click=${() => this.shadowRoot.getElementById("fileInput").click()} @drop=${this._handleFileDrop} @dragover=${this._handleDragOver}>
              ${this.newFontFile ? html`<strong>Selected:</strong> ${this.newFontFile.name}` : html`Click to select file or drag here`}
            </div>
            <input type="file" id="fileInput" hidden accept=".ttf,.otf,.woff,.woff2" @change=${this._handleFileSelect} />

            <div class="form-group" style="margin-top: 16px;">
              <label>Font Name</label>
              <input type="text" .value=${this.newFontName} @input=${(e) => (this.newFontName = e.target.value)} placeholder="e.g. Brand Heading Font" required />
            </div>

            <button type="submit" class="btn btn-primary" ?disabled=${this.isLoading}>Upload & Add</button>
          </form>
        </div>

        <div class="import-section">
          <h3>Import from URL</h3>
          <p style="color: var(--secondary-color); font-size: 14px; margin-bottom: 16px;">Enter a direct link to a font file or CSS URL (Google Fonts, CDNFonts).</p>
          <form @submit=${this._handleUrlImport}>
            <!--  <div class="form-group">
              <label>Font Name (Optional if URL provides metadata)</label>
              <input type="text" .value=${this.newFontName} @input=${(e) => (this.newFontName = e.target.value)} placeholder="e.g., My Custom Font" />
            </div> -->

            <div class="form-group">
              <label>Font URL</label>
              <input type="url" .value=${this.newFontUrl} @input=${(e) => (this.newFontUrl = e.target.value)} placeholder="https://fonts.cdnfonts.com/css/..." required />
            </div>

            <button type="submit" class="btn btn-primary" ?disabled=${this.isLoading}>Import Font</button>
          </form>
        </div>
      </div>
    `;
  }

  render() {
    // Calculate Counts for Tabs
    const installedCount = this.fonts.filter((f) => f.installed).length;
    const marketCount = this.fonts.filter((f) => f.type === "predefined").length;

    // Prepare List for Active Tab
    const listToRender = this._getFilteredFonts();

    const managerUI = html` <div class="manager-container">
      <div class="header">
        <h2>Font Manager</h2>
        <div style="display: flex; gap: 10px; align-items: center;">${this.asDialog ? html`<button class="btn-close" @click=${this.close} title="Close">&times;</button>` : nothing}</div>
      </div>

      <div class="toolbar">
        ${this.enableSearch
          ? html`
              <div class="search-input-wrapper">
                <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input type="text" class="search-input" placeholder="Search fonts..." .value=${this.searchQuery} @keyup=${this._handleSearch} />
              </div>
            `
          : nothing}
        ${this.enableSorting
          ? html`
              <select class="sort-select" .value=${this.sortOption} @change=${this._handleSort}>
                <option value="popular">Most Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="name">Name (A-Z)</option>
                <option value="newest">Newest</option>
              </select>
            `
          : nothing}
      </div>

      <div class="tabs">
        <button class="tab-btn ${this.activeTab === "installed" ? "active" : ""}" @click=${() => this._handleTabChange("installed")}>Installed <span class="tab-badge">${installedCount}</span></button>
        <button class="tab-btn ${this.activeTab === "marketplace" ? "active" : ""}" @click=${() => this._handleTabChange("marketplace")}>Marketplace <span class="tab-badge">${marketCount}</span></button>
        <button class="tab-btn ${this.activeTab === "import" ? "active" : ""}" @click=${() => this._handleTabChange("import")}>Import</button>
      </div>

      ${this.isLoading
        ? html` <div class="loading-overlay">
            <span class="loader loader-lg"></span>
            <p style="margin-top:1rem; color:var(--secondary-color)">Processing...</p>
          </div>`
        : nothing}
      ${this.activeTab === "import" ? this.renderImportPage() : html` <div id="virtual-scroll-container" class="content" @scroll=${this._handleVirtualScroll}>${this.renderVirtualGrid(listToRender)}</div> `}
      ${this.asDialog
        ? html` <div class="footer">
            ${this.errorMessage ? html`<div class="error-msg">${this.errorMessage}</div>` : nothing}
            <button class="btn btn-secondary" style="flex: 0 0 auto; width: auto;" @click=${this.close}>Close</button>
            <button class="btn btn-primary" style="flex: 0 0 auto; width: auto;" ?disabled=${!this.selectedFont} @click=${this.close}>Select</button>
          </div>`
        : nothing}
    </div>`;

    if (this.asDialog) {
      if (!this.isOpen) return nothing;
      return html`<div class="dialog-overlay" @click=${this._handleOverlayClick}>${managerUI}</div>`;
    }
    return managerUI;
  }
}

customElements.define("rd-font-manager", RDFontManager);
